import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, MapContainer, Marker, Pane, TileLayer, Tooltip, ZoomControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

const BOTTLE_MESSAGE_ICON_URL = 'https://app.pfandpirat.com/wp-content/uploads/Alte-Flaschenpost.png';
const ICON_CACHE = new Map();
const CLUSTER_ICON_CACHE = new Map();

function buildItemKey(item) {
  return `${item?.sourceType || 'item'}-${item?.id ?? 'unknown'}`;
}

function getCachedIcon(cacheKey, factory) {
  if (!ICON_CACHE.has(cacheKey)) {
    ICON_CACHE.set(cacheKey, factory());
  }

  return ICON_CACHE.get(cacheKey);
}

function buildIcon({ iconUrl, selected = false, className = 'pfandpirat-map-pin' }) {
  const cacheKey = `${className}|${iconUrl || 'default'}|${selected ? 'selected' : 'normal'}`;

  return getCachedIcon(cacheKey, () => {
    if (!iconUrl) {
      return L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: selected ? [31, 49] : [25, 41],
        iconAnchor: selected ? [15, 49] : [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
        className: `${className}${selected ? ` ${className}--selected` : ''}`,
      });
    }

    const size = selected ? 56 : 44;
    const anchorX = Math.round(size / 2);
    const anchorY = size;

    return L.icon({
      iconUrl,
      iconRetinaUrl: iconUrl,
      iconSize: [size, size],
      iconAnchor: [anchorX, anchorY],
      popupAnchor: [0, -anchorY],
      className: `${className}${selected ? ` ${className}--selected` : ''}`,
    });
  });
}

function buildClusterIcon({ count, selected = false }) {
  const bucket = count >= 100 ? 'xl' : count >= 25 ? 'lg' : count >= 10 ? 'md' : 'sm';
  const sizeMap = { sm: 42, md: 48, lg: 56, xl: 64 };
  const size = sizeMap[bucket] || 42;
  const cacheKey = `cluster|${bucket}|${selected ? 'selected' : 'normal'}`;

  if (!CLUSTER_ICON_CACHE.has(cacheKey)) {
    CLUSTER_ICON_CACHE.set(
      cacheKey,
      L.divIcon({
        html: '<span class="treasure-map-cluster-marker__count"></span>',
        className: `treasure-map-cluster-marker treasure-map-cluster-marker--${bucket}${selected ? ' is-selected' : ''}`,
        iconSize: [size, size],
        iconAnchor: [Math.round(size / 2), Math.round(size / 2)],
      })
    );
  }

  const template = CLUSTER_ICON_CACHE.get(cacheKey);
  return L.divIcon({
    html: `<span class="treasure-map-cluster-marker__count">${count}</span>`,
    className: template.options.className,
    iconSize: template.options.iconSize,
    iconAnchor: template.options.iconAnchor,
  });
}

function getClusterGridSize(zoom) {
  if (zoom >= 18) return 0;
  if (zoom >= 17) return 44;
  if (zoom >= 16) return 58;
  if (zoom >= 15) return 74;
  if (zoom >= 14) return 88;
  if (zoom >= 13) return 108;
  return 128;
}

function getItemPosition(item) {
  const latitude = Number(item?.latitude ?? item?.lat);
  const longitude = Number(item?.longitude ?? item?.lng);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return [latitude, longitude];
}

function isItemInsideBounds(item, bounds) {
  const position = getItemPosition(item);
  if (!position || !bounds) {
    return false;
  }

  return bounds.contains(L.latLng(position[0], position[1]));
}

function normalizeViewportBounds(bounds) {
  if (!bounds || typeof bounds.getSouth !== 'function') {
    return null;
  }

  const paddedBounds = bounds.pad(0.35);

  return {
    south: Number(paddedBounds.getSouth().toFixed(5)),
    west: Number(paddedBounds.getWest().toFixed(5)),
    north: Number(paddedBounds.getNorth().toFixed(5)),
    east: Number(paddedBounds.getEast().toFixed(5)),
  };
}

function RelocateButton({ userPos }) {
  const map = useMap();

  return (
    <button
      onClick={() => {
        if (userPos) map.setView(userPos, 16, { animate: true });
      }}
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 999,
        padding: '7px 12px',
        borderRadius: 8,
        background: '#f8faff',
        boxShadow: '0 1px 8px #0002',
        fontWeight: 'bold',
        border: '1px solid #ddd',
        cursor: 'pointer',
        fontSize: '1.08em',
      }}
      title="Karte auf meinen Standort zentrieren"
    >
      📍 Standort
    </button>
  );
}

function SelectionFollower({ selectedItem, shouldFollow = false }) {
  const map = useMap();
  const lastFollowedKeyRef = React.useRef('');

  useEffect(() => {
    if (!shouldFollow || !selectedItem) {
      return;
    }

    const selectedKey = buildItemKey(selectedItem);
    if (selectedKey === lastFollowedKeyRef.current) {
      return;
    }

    const position = getItemPosition(selectedItem);
    if (!position) {
      return;
    }

    const currentBounds = map.getBounds();
    const currentZoom = map.getZoom();
    const targetLatLng = L.latLng(position[0], position[1]);

    lastFollowedKeyRef.current = selectedKey;

    if (currentBounds?.contains?.(targetLatLng)) {
      if (currentZoom < 17) {
        map.flyTo(position, 17, {
          animate: true,
          duration: 0.45,
        });
      }
      return;
    }

    map.flyTo(position, Math.max(currentZoom, 16), {
      animate: true,
      duration: 0.6,
    });
  }, [map, selectedItem, shouldFollow]);

  return null;
}

function MarkerTooltip({ title, categoryLabel, selected }) {
  return (
    <Tooltip direction="top" offset={[0, -36]} opacity={selected ? 1 : 0.92} permanent={selected}>
      <div className="treasure-map-marker-tooltip">
        <strong>{title}</strong>
        {categoryLabel ? <div>{categoryLabel}</div> : null}
      </div>
    </Tooltip>
  );
}

function MapPickHandler({ isMapPickMode, onPickLocation }) {
  const map = useMapEvents({
    click(event) {
      if (!isMapPickMode) {
        return;
      }

      const latitude = Number(event?.latlng?.lat);
      const longitude = Number(event?.latlng?.lng);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return;
      }

      onPickLocation?.({
        latitude: Number(latitude.toFixed(6)),
        longitude: Number(longitude.toFixed(6)),
      });
    },
  });

  useEffect(() => {
    const container = map.getContainer();
    const previousCursor = container.style.cursor;
    container.style.cursor = isMapPickMode ? 'crosshair' : previousCursor || '';

    return () => {
      container.style.cursor = previousCursor || '';
    };
  }, [map, isMapPickMode]);

  return null;
}

function ViewportSync({ onViewportChange }) {
  const map = useMap();

  const emitViewport = useCallback(() => {
    if (typeof onViewportChange !== 'function') {
      return;
    }

    const bounds = map.getBounds();
    if (!bounds?.isValid?.()) {
      return;
    }

    onViewportChange({ ...normalizeViewportBounds(bounds), zoom: map.getZoom() });
  }, [map, onViewportChange]);

  useEffect(() => {
    emitViewport();
    map.on('moveend zoomend', emitViewport);

    return () => {
      map.off('moveend zoomend', emitViewport);
    };
  }, [map, emitViewport]);

  return null;
}

function LiveUserLocation({ position, accuracy }) {
  const markerRef = useRef(null);
  const safeAccuracy = Number(accuracy);

  useEffect(() => {
    if (!markerRef.current || !Array.isArray(position) || position.length !== 2) {
      return;
    }

    markerRef.current.setLatLng(position);
  }, [position]);

  if (!Array.isArray(position) || position.length !== 2) {
    return null;
  }

  return (
    <Pane name="pfandpirat-user-location-pane" style={{ zIndex: 450, pointerEvents: 'none' }}>
      {Number.isFinite(safeAccuracy) && safeAccuracy > 0 && (
        <Circle
          center={position}
          radius={safeAccuracy}
          interactive={false}
          pathOptions={{
            color: '#2563eb',
            fillColor: '#60a5fa',
            fillOpacity: 0.16,
            opacity: 0.65,
            weight: 1,
          }}
        />
      )}
      <Marker
        ref={markerRef}
        position={position}
        interactive={false}
        zIndexOffset={-1000}
        icon={L.icon({
          iconUrl: 'https://app.pfandpirat.com/wp-content/uploads/pfandpirat.png',
          iconSize: [50, 50],
          iconAnchor: [25, 50],
          className: 'pfandpirat-user-location-marker',
        })}
      >
        <Tooltip direction="top" offset={[0, -36]} permanent interactive={false}>
          <div className="treasure-map-marker-tooltip">
            <strong>Du bist hier</strong>
          </div>
        </Tooltip>
      </Marker>
    </Pane>
  );
}

function ClusteredTreasureMarkers({ items, selectedItemKey, onSelectItem, categoryIcons }) {
  const map = useMap();
  const [viewState, setViewState] = useState(() => ({
    zoom: map.getZoom(),
    bounds: map.getBounds(),
  }));

  useEffect(() => {
    const sync = () => {
      setViewState({
        zoom: map.getZoom(),
        bounds: map.getBounds(),
      });
    };

    sync();
    map.on('moveend zoomend', sync);

    return () => {
      map.off('moveend zoomend', sync);
    };
  }, [map]);

  const clusteredEntries = useMemo(() => {
    const safeItems = Array.isArray(items) ? items : [];
    const bounds = viewState.bounds?.pad?.(0.15) || viewState.bounds;
    const visibleItems = safeItems.filter((item) => isItemInsideBounds(item, bounds));
    const gridSize = getClusterGridSize(viewState.zoom);

    if (gridSize <= 0) {
      return visibleItems.map((item) => ({
        type: 'item',
        key: buildItemKey(item),
        item,
      }));
    }

    const buckets = new Map();

    visibleItems.forEach((item) => {
      const position = getItemPosition(item);
      if (!position) {
        return;
      }

      const point = map.project(L.latLng(position[0], position[1]), viewState.zoom);
      const bucketKey = `${Math.floor(point.x / gridSize)}:${Math.floor(point.y / gridSize)}`;

      if (!buckets.has(bucketKey)) {
        buckets.set(bucketKey, {
          key: bucketKey,
          items: [],
          sumLat: 0,
          sumLng: 0,
        });
      }

      const bucket = buckets.get(bucketKey);
      bucket.items.push(item);
      bucket.sumLat += position[0];
      bucket.sumLng += position[1];
    });

    return Array.from(buckets.values()).map((bucket) => {
      if (bucket.items.length === 1) {
        return {
          type: 'item',
          key: buildItemKey(bucket.items[0]),
          item: bucket.items[0],
        };
      }

      const count = bucket.items.length;
      const containsSelected = bucket.items.some((entry) => buildItemKey(entry) === selectedItemKey);

      return {
        type: 'cluster',
        key: `cluster-${viewState.zoom}-${bucket.key}`,
        count,
        latitude: bucket.sumLat / count,
        longitude: bucket.sumLng / count,
        items: bucket.items,
        containsSelected,
      };
    });
  }, [items, map, selectedItemKey, viewState.bounds, viewState.zoom]);

  return clusteredEntries.map((entry) => {
    if (entry.type === 'cluster') {
      const icon = buildClusterIcon({ count: entry.count, selected: entry.containsSelected });

      return (
        <Marker
          key={entry.key}
          position={[entry.latitude, entry.longitude]}
          icon={icon}
          eventHandlers={{
            click: () => {
              map.flyTo([entry.latitude, entry.longitude], Math.min(map.getZoom() + 2, 19), {
                animate: true,
                duration: 0.45,
              });
            },
          }}
        >
          <Tooltip direction="top" offset={[0, -22]} opacity={0.95}>
            <div className="treasure-map-cluster-tooltip">
              <strong>{entry.count} Punkte</strong>
              <div>Klicken zum Reinzoomen</div>
            </div>
          </Tooltip>
        </Marker>
      );
    }

    const item = entry.item;
    const position = getItemPosition(item);
    if (!position) {
      return null;
    }

    const selected = entry.key === selectedItemKey;
    const iconUrl =
      item.sourceType === 'donation'
        ? categoryIcons?.[item.categoryLabel || item.category] || categoryIcons?.Pfandspende || item.iconUrl || ''
        : item.sourceType === 'bottle_message'
          ? BOTTLE_MESSAGE_ICON_URL
          : item.iconUrl || categoryIcons?.[item.category] || '';
    const className = item.sourceType === 'bottle_message' ? 'pfp-bottle-message-marker-icon' : 'pfandpirat-map-pin';
    const icon = buildIcon({ iconUrl, selected, className });

    return (
      <Marker
        key={entry.key}
        position={position}
        icon={icon}
        eventHandlers={{ click: () => onSelectItem?.(item) }}
      >
        <MarkerTooltip
          title={item.title || item.name || 'Punkt'}
          categoryLabel={item.categoryLabel || item.category || ''}
          selected={selected}
        />
      </Marker>
    );
  });
}

export function TrashcanMap({
  ready,
  startCenter,
  userPos,
  userLocation = null,
  pfandSpenden,
  allLocations,
  bottleMessages,
  selectedItem,
  selectedItemKey,
  shouldFollowSelectedItem = false,
  onSelectItem,
  onViewportChange,
  categoryIcons,
  isMapPickMode = false,
  onPickLocation,
  draftLocation = null,
  showDraftLocationPreview = false,
}) {
  const safeLocations = Array.isArray(allLocations) ? allLocations : [];
  const safeDonations = Array.isArray(pfandSpenden) ? pfandSpenden : [];
  const safeBottleMessages = Array.isArray(bottleMessages) ? bottleMessages : [];
  const draftLocationIcon = useMemo(() => buildIcon({ className: 'pfp-draft-location-marker-icon', selected: true }), []);
  const liveUserPosition = useMemo(() => {
    const latitude = Number(userLocation?.latitude);
    const longitude = Number(userLocation?.longitude);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return [latitude, longitude];
    }

    return Array.isArray(userPos) && userPos.length === 2 ? userPos : null;
  }, [userLocation, userPos]);

  const combinedItems = useMemo(
    () => [...safeDonations, ...safeLocations, ...safeBottleMessages],
    [safeDonations, safeLocations, safeBottleMessages]
  );

  if (!ready) return null;

  return (
    <MapContainer
      center={startCenter}
      zoom={16}
      scrollWheelZoom={true}
      style={{
        height: '65vh',
        borderRadius: 18,
        boxShadow: '0 2px 16px #0001',
        marginBottom: 0,
      }}
      zoomControl={false}
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
      <ZoomControl position="topright" />
      <RelocateButton userPos={liveUserPosition} />
      <SelectionFollower selectedItem={selectedItem} shouldFollow={shouldFollowSelectedItem} />
      <MapPickHandler isMapPickMode={isMapPickMode} onPickLocation={onPickLocation} />
      <ViewportSync onViewportChange={onViewportChange} />

      {isMapPickMode && (
        <div className="treasure-map-map-pick-hint">
          Karten-Pick aktiv · Klicke auf die Karte, um den neuen Ort zu setzen.
        </div>
      )}

      {showDraftLocationPreview && Number.isFinite(Number(draftLocation?.latitude)) && Number.isFinite(Number(draftLocation?.longitude)) && (
        <Marker
          position={[Number(draftLocation.latitude), Number(draftLocation.longitude)]}
          icon={draftLocationIcon}
          interactive={false}
        >
          <Tooltip direction="top" offset={[0, -36]} permanent>
            <div className="treasure-map-marker-tooltip">
              <strong>Neuer Ort</strong>
              <div>Vorschau</div>
            </div>
          </Tooltip>
        </Marker>
      )}

      <ClusteredTreasureMarkers
        items={combinedItems}
        selectedItemKey={selectedItemKey}
        onSelectItem={onSelectItem}
        categoryIcons={categoryIcons}
      />

      <LiveUserLocation position={liveUserPosition} accuracy={userLocation?.accuracy} />
    </MapContainer>
  );
}

export default TrashcanMap;
